import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  StackLayout,
  HeadingText,
  ParagraphText,
  Button,
  useMediaQuery,
  queryHelpers,
  Modal,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { captureError } from '@leagueplatform/observability';
import { reportAgentPermissionError } from '@leagueplatform/core';
import { ContentProps } from 'models/profile-settings.model';
import { sendProfileSettingsButtonClickedEvent } from 'utils/analytics-events';
import { isEditRestricted } from 'utils/is-edit-restricted';
import { UnsaveChangesWarningModal } from 'components/modals/unsave-changes-warning-modal.component';
import { DetailItem } from './details-item.component';

interface DetailSectionProps {
  title: string;
  description?: string;
  contents: ContentProps[];
  ssoUrl?: string;
  onSave?: (values: Record<string, string>) => Promise<void> | void;
  renderEditContent?: (props: {
    contents: ContentProps[];
    editValues: Record<string, string>;
    onChange: (id: string, value: string) => void;
    isMobile: boolean;
  }) => React.ReactNode;
  modals?: React.ReactNode;
  formatValue?: (value: string, labelId: string) => string;
  alwaysEdit?: boolean;
  showBorder?: boolean;
  hideButtons?: boolean;
  noPadding?: boolean;
  disableUnsavedModal?: boolean;
  reset?: () => void;
  canSave?: boolean;
}

export const DetailSection: FC<DetailSectionProps> = ({
  title,
  description,
  contents,
  ssoUrl,
  onSave,
  renderEditContent,
  modals,
  formatValue = (value) => value,
  alwaysEdit = false,
  showBorder = true,
  hideButtons = false,
  disableUnsavedModal = false,
  noPadding = false,
  reset,
  canSave = true,
}) => {
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  const { formatMessage } = useIntl();
  const editRestricted = isEditRestricted();
  const [isEditing, setIsEditing] = useState(alwaysEdit);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const initialValues = useMemo(
    () =>
      Object.fromEntries(contents.map((item) => [item.labelId, item.value])),
    [contents],
  );

  const [editValues, setEditValues] =
    useState<Record<string, string | boolean>>(initialValues);

  const t = useCallback(
    (id: string) => formatMessage({ id: id as never }),
    [formatMessage],
  );

  const hasUnsavedChanges = useMemo(
    () =>
      Object.keys(editValues).some(
        (key) => editValues[key] !== initialValues[key],
      ),
    [editValues, initialValues],
  );

  const handleInputChange = useCallback(
    (id: string, value: string | boolean) => {
      const content = contents.find((item) => item.labelId === id);
      const isBoolean = content?.isBoolean ?? false;

      const parsedValue = isBoolean
        ? value === 'true' || value === true
        : value;
      setEditValues((prev) => ({ ...prev, [id]: parsedValue }));
    },
    [contents],
  );

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    try {
      sendProfileSettingsButtonClickedEvent('save');
      await onSave(editValues as never);
      if (!alwaysEdit) setIsEditing(false);
    } catch (error) {
      captureError(new Error(`Failed to save profile settings: ${error}`));
    }
  }, [editValues, onSave, alwaysEdit]);

  const handleCancel = useCallback(() => {
    sendProfileSettingsButtonClickedEvent('back');
    if (hasUnsavedChanges && !disableUnsavedModal) {
      setShowUnsavedModal(true);
    } else {
      setEditValues(initialValues);
      setIsEditing(false);
    }
  }, [hasUnsavedChanges, initialValues, disableUnsavedModal]);

  const handleEditClick = useCallback(() => {
    if (editRestricted) {
      reportAgentPermissionError(true, undefined, 'agent');
      return;
    }
    if (!ssoUrl) {
      if (isEditing) {
        handleCancel();
      } else {
        sendProfileSettingsButtonClickedEvent('edit personal information');
        setIsEditing(true);
      }
    }
  }, [editRestricted, ssoUrl, isEditing, handleCancel]);

  const handleUnsavedConfirm = useCallback(() => {
    setEditValues(initialValues);
    reset?.();
    setIsEditing(false);
    setShowUnsavedModal(false);
  }, [initialValues, reset]);

  const handleUnsavedSave = () => setShowUnsavedModal(false);

  const renderedContent = useMemo(() => {
    if (!isEditing || !renderEditContent) {
      return contents.length > 0 ? (
        contents.map((content, index) => (
          <DetailItem
            key={content.labelId}
            label={t(content.labelId)}
            value={formatValue(String(content.value), content.labelId)}
            isLastItem={index === contents.length - 1}
          />
        ))
      ) : (
        <ParagraphText>{t('STR_NO_CONTENT')}</ParagraphText>
      );
    }

    const stringEditValues = Object.fromEntries(
      Object.entries(editValues).map(([key, value]) => [key, String(value)]),
    );

    return renderEditContent({
      contents,
      editValues: stringEditValues,
      onChange: (id: string, value: string) => handleInputChange(id, value),
      isMobile,
    });
  }, [
    isEditing,
    contents,
    editValues,
    handleInputChange,
    isMobile,
    renderEditContent,
    formatValue,
    t,
  ]);

  return (
    <StackLayout
      css={{
        width: '100%',
        margin: '$half auto',
        border: showBorder ? '1px solid $onSurfaceBorderSubdued' : 'none',
        borderRadius: '$card',
        overflow: 'hidden',
        padding: noPadding ? '0' : '$two $oneAndHalf',
        backgroundColor: '$surfaceCardSecondary',
      }}
    >
      <StackLayout
        orientation="horizontal"
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '$half',
          width: '100%',
        }}
      >
        <HeadingText
          level="2"
          size="md"
          css={{ fontSize: '20px', marginBottom: '$one' }}
        >
          {t(title)}
        </HeadingText>
        {!hideButtons && (
          <Button
            icon="interfaceEdit"
            as={ssoUrl && !editRestricted ? 'a' : undefined}
            href={ssoUrl && !editRestricted ? ssoUrl : undefined}
            onClick={handleEditClick}
            priority="secondary"
            css={{
              height: '$two',
              paddingX: '0',
              fontWeight: '600',
              fontSize: '$buttonTwo',
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '$interactiveActionHovered',
              },
              '&:focus': {
                outline: 'none !important',
              },
            }}
          >
            {t(isEditing ? 'STR_CANCEL_EDIT' : 'STR_EDIT')}
          </Button>
        )}
      </StackLayout>
      {description && !isEditing && (
        <ParagraphText
          css={{
            fontSize: '$bodyTwo',
            marginBottom: '$one',
          }}
        >
          {t(description)}
        </ParagraphText>
      )}

      {renderedContent}

      {isEditing && !hideButtons && (
        <StackLayout
          orientation={{ '@initial': 'horizontal', '@mobile': 'vertical' }}
          css={{
            justifyContent: 'flex-end',
            gap: '$one',
            marginTop: '$oneAndHalf',
            width: '100%',
          }}
        >
          <Button
            onClick={handleCancel}
            priority="secondary"
            css={{
              height: '$three',
              display: 'flex',
              justifyContent: 'center',
              width: isMobile ? '100%' : 'auto',
              backgroundColor: 'transparent',
              '&:hover, &:active, &:focus': {
                backgroundColor: '$interactiveBackgroundHovered',
                color: '$interactiveActionHovered',
                textDecoration: 'none',
              },
            }}
          >
            {t('STR_CANCEL_EDIT')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            css={{
              height: '$three',
              display: 'flex',
              justifyContent: 'center',
              width: isMobile ? '100%' : 'auto',
              opacity: canSave ? 1 : 0.5,
              '&:hover, &:active, &:focus': {
                backgroundColor: '$interactiveBackgroundHovered',
                color: '$interactiveActionHovered',
                textDecoration: 'none',
              },
              '&:disabled': {
                cursor: 'not-allowed',
                opacity: 0.5,
              },
            }}
          >
            {t('STR_SAVE_CHANGES')}
          </Button>
        </StackLayout>
      )}

      {!disableUnsavedModal && (
        <Modal.Root open={showUnsavedModal} onOpenChange={handleUnsavedSave}>
          <UnsaveChangesWarningModal
            title={t('STR_UNSAVE_CHANGES_TITLE')}
            description={t('STR_UNSAVE_CHANGES_DESCRIPTION')}
            cancelButtonText={t('STR_UNSAVE_CHANGES_CANCEL')}
            confirmButtonText={t('STR_UNSAVE_CHANGES_CONFIRM')}
            onConfirm={handleUnsavedConfirm}
            onClose={handleUnsavedSave}
          />
        </Modal.Root>
      )}

      {modals}
    </StackLayout>
  );
};
